import type { MetaFunction } from "@remix-run/node";
import { motion } from "motion/react";
import { Button } from "@/app/components/ui/button";
import { Input } from "@/app/components/ui/input";
import { Card } from "@/app/components/ui/card";
import { ArrowLeft, Loader2, Mail } from "lucide-react";
import { useActionData, useNavigation, Form, Link } from "@remix-run/react";
import { useTranslation } from "react-i18next";
import { ROUTES } from "@/app/utils/constants";
import { z } from "zod";
import { prisma } from "@/server/lib/prisma";
import { sendPasswordResetEmail } from "@/server/services/emailService";
import { handleFormAction } from "@/app/utils/remix-helpers.server";

export const meta: MetaFunction = () => {
  return [
    { title: "Forgot Password | AiAuditBuddy" },
    { name: "description", content: "Reset your AiAuditBuddy password" },
  ];
};

const ForgotPasswordSchema = z.object({
  email: z
    .string()
    .min(1, "Email is required")
    .email("Please enter a valid email address"),
});

interface ActionData {
  success?: boolean;
  message?: string;
  errors?: {
    email?: string[];
  };
}

// TODO: Add rate limiting to prevent abuse (e.g., max 3 requests per email per hour)
export const action = handleFormAction(
  { form: ForgotPasswordSchema },
  async ({ formData }) => {
    const normalizedEmail = formData.email.toLowerCase();

    // Check if user exists
    const user = await prisma.user.findUnique({
      where: { email: normalizedEmail },
    });

    // Check if user uses OAuth (Google)
    if (user && user.oauthProvider) {
      return {
        success: false,
        message:
          "This account uses Google Sign-In. Please use the 'Continue with Google' button to log in.",
      };
    }

    // Send email only if user exists (and is not OAuth)
    if (user) {
      try {
        await sendPasswordResetEmail(user.email);
      } catch (error) {
        console.error("Failed to send password reset email:", error);
        // Don't throw error - we don't want to reveal if user exists
        // Continue and show same success message
      }
    }

    // Always return same success message for email/password users
    return {
      success: true,
      message:
        "If an account exists with this email, you will receive password reset instructions.",
    };
  }
);

export default function ForgotPasswordPage() {
  const { t } = useTranslation();
  const actionData = useActionData<ActionData>();
  const navigation = useNavigation();
  const isSubmitting = navigation.state === "submitting";

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Background */}
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-80"
        style={{ backgroundImage: 'url("/assets/login-background.png")' }}
      />

      {/* Logo in Upper Left */}
      <motion.div
        className="absolute top-8 left-8 z-20"
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{
          duration: 0.6,
          ease: [0.23, 1, 0.32, 1],
        }}
      >
        <Link to="/" className="flex items-center gap-3">
          <img
            src="/assets/logo.png"
            alt={t("AiAuditBuddy Logo")}
            className="w-12 h-12 rounded-2xl"
          />
          <h1 className="text-[18px] font-semibold text-gray-900">
            AiAuditBuddy
          </h1>
        </Link>
      </motion.div>

      {/* Form Container */}
      <div className="min-h-screen flex items-center justify-center px-8 relative">
        <motion.div
          initial={{ opacity: 0, y: 20, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{
            duration: 0.6,
            ease: [0.23, 1, 0.32, 1],
          }}
          className="w-full max-w-[480px]"
        >
          {/* Header */}
          <div className="text-center mb-8">
            <motion.h2
              className="text-[28px] font-semibold text-gray-900"
              style={{ letterSpacing: "-0.025em" }}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.5 }}
            >
              {t("Forgot Password")}
            </motion.h2>
            <motion.p
              className="text-gray-500 text-[15px] mt-2"
              style={{ letterSpacing: "-0.01em" }}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.5 }}
            >
              {t("Enter your email to receive a password reset link")}
            </motion.p>
          </div>

          {/* Form Card */}
          <Card className="p-10 border border-transparent bg-white/30 backdrop-blur-md rounded-2xl shadow-none overflow-hidden">
            <motion.div
              className="space-y-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.5 }}
            >
              {/* Success Message */}
              {actionData?.success && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm"
                >
                  <div className="flex gap-2">
                    <Mail className="h-5 w-5 flex-shrink-0" />
                    <p>{actionData.message}</p>
                  </div>
                </motion.div>
              )}

              <Form method="post" className="space-y-6">
                {/* Email Field */}
                <div className="space-y-2">
                  <label
                    htmlFor="email"
                    className="text-[13px] font-medium text-gray-900 block"
                    style={{ letterSpacing: "-0.005em" }}
                  >
                    {t("Email")}
                  </label>
                  <Input
                    id="email"
                    name="email"
                    autoComplete="email"
                    type="email"
                    placeholder={t("Enter your email")}
                    className="h-11 bg-white/70 border-1 border-white rounded-lg text-[15px] placeholder:text-gray-400 focus:border-none focus-visible:border-none focus-visible:bg-white focus-visible:ring-2 focus-visible:ring-blue-500/20 focus-visible:ring-offset-0 transition-all duration-200 hover:bg-white"
                    required
                  />
                  {actionData?.errors?.email && (
                    <p className="text-red-500 text-sm">
                      {actionData.errors.email[0]}
                    </p>
                  )}
                </div>

                {/* General Error */}
                {actionData?.message && !actionData?.success && (
                  <div className="text-red-500 text-sm bg-red-50 p-3 rounded-lg border border-red-200">
                    {actionData.message}
                  </div>
                )}

                {/* Submit Button */}
                <motion.div
                  whileHover={{ scale: 1.0 }}
                  whileTap={{ scale: 0.99 }}
                  transition={{
                    type: "spring",
                    stiffness: 300,
                    damping: 25,
                  }}
                >
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-[#287BFF] text-white hover:bg-[#287BFF]/90 shadow-sm hover:shadow-sm h-11 px-6 gap-2 font-medium rounded-lg transition-all duration-200"
                  >
                    {isSubmitting ? (
                      <div className="flex items-center gap-2">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        <span>{t("Sending...")}</span>
                      </div>
                    ) : (
                      t("Send Reset Link")
                    )}
                  </Button>
                </motion.div>
              </Form>

              {/* Back to Login */}
              <div className="text-center pt-4">
                <Link
                  to={ROUTES.LOGIN}
                  className="inline-flex items-center gap-2 text-[13px] text-gray-600 hover:text-gray-900 transition-colors duration-200"
                >
                  <ArrowLeft className="h-4 w-4" />
                  {t("Back to Login")}
                </Link>
              </div>
            </motion.div>
          </Card>
        </motion.div>
      </div>
    </div>
  );
}
